import { useCallback, useState } from 'react';

import parseAPIResponse, { ApiError, isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

export interface MutationOptions<TData, TVariables, TError = ApiError> {
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
  onError?: (error: TError, variables: TVariables, rawResponse: string) => void | Promise<void>;
  isResponseSuccessful?: (response: TData) => boolean;
  errors?: Record<string, string>;
}

export interface MutationResult<TData, TVariables, TError = ApiError> {
  mutate: (variables: TVariables) => void;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: TError | null;
  data: TData | undefined;
  reset: () => void;
}

type UseMutationProps<TData = unknown, TVariables = unknown, TError = ApiError> = Partial<
  MutationOptions<TData, TVariables, TError>
> & {
  mutationFn: (variables: TVariables) => Promise<string>;
};

function useMutation<TData = unknown, TVariables = unknown, TError = ApiError>({
  mutationFn,
  ...options
}: UseMutationProps<TData, TVariables, TError>): MutationResult<TData, TVariables, TError> {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<TError | null>(null);
  const [data, setData] = useState<TData | undefined>(undefined);

  const reset = useCallback(() => {
    setIsLoading(false);
    setIsError(false);
    setIsSuccess(false);
    setError(null);
    setData(undefined);
  }, []);

  /**
   * Use mutateAsync when you need to await the response
   */
  const mutateAsync = useCallback(
    async (variables: TVariables): Promise<TData> => {
      setIsLoading(true);
      setIsError(false);
      setIsSuccess(false);
      setError(null);

      let response: string | undefined;

      try {
        response = await mutationFn(variables);

        const { error: parsedError, parsedResponse } = parseAPIResponse<TData>(response, {
          isSuccessful: options.isResponseSuccessful || isDefaultSuccessResponse,
          errors: options.errors,
        });

        if (parsedError) {
          const error = parsedError as TError;
          setError(error);
          setIsError(true);
          setIsLoading(false);

          throw new ApiError(parsedError.type, parsedError.message);
        }

        if (parsedResponse) {
          setData(parsedResponse);
          setIsSuccess(true);
          setIsLoading(false);

          await options.onSuccess?.(parsedResponse, variables);

          return parsedResponse;
        }

        throw new Error('No response data received');
      } catch (err) {
        const error = err as TError;
        setError(error);
        setIsError(true);
        setIsLoading(false);

        await options.onError?.(error, variables, response || '');

        throw error;
      }
    },
    [mutationFn, options],
  );

  /**
   * Use mutate when you don't need to await the response
   * You can react to the result of the mutation with the callbacks (onSuccess, onError)
   * or use the isError, isSuccess, error variables
   */
  const mutate = useCallback(
    (variables: TVariables): void => {
      mutateAsync(variables).catch(() => {
        // Errors are handled in mutateAsync and stored in state
        // No need to re-throw here
      });
    },
    [mutateAsync],
  );

  return {
    mutate,
    mutateAsync,
    isLoading,
    isError,
    isSuccess,
    error,
    data,
    reset,
  };
}

export default useMutation;
